import { motion } from "framer-motion";
import { Leaf, Heart, Users, Award, Sprout, Target, Eye } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/SEO";

const values = [
  {
    icon: Leaf,
    title: "Fresh Harvest",
    description: "Harvested fresh on the day of delivery for maximum nutrition and flavor."
  },
  {
    icon: Heart,
    title: "Chemical Free",
    description: "100% chemical-free practices with no synthetic pesticides or fertilizers."
  },
  {
    icon: Users,
    title: "Locally Grown",
    description: "Grown in our local urban farm, supporting sustainable agriculture."
  },
  {
    icon: Award,
    title: "Nutrient Dense",
    description: "Up to 40x more nutrients than mature vegetables in every serving."
  }
];

export default function AboutPage() {
  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="about-page">
      <SEO title="About Us" description="Learn about Praroha Greens - cultivating fresh, sustainable microgreens with a vision for a healthier, greener future." keywords="about praroha greens, microgreens company, organic farming" path="/about" />
      {/* Compact Hero */}
      <section className="py-10 md:py-14 bg-praroha-forest" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
              <span className="inline-block px-4 py-1.5 bg-white/10 text-white/90 text-sm font-medium rounded-full mb-4">
                Our Story
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                Growing Wellness,
                <span className="block text-praroha-sage">One Seed at a Time</span>
              </h1>
              <p className="text-white/60 text-sm leading-relaxed max-w-md">
                Praroha Greens was born from a simple belief: everyone deserves access to the freshest, 
                most nutritious food possible. What started as a small urban farming experiment has 
                grown into a mission to revolutionize how India thinks about nutrition.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { value: "25+", label: "Varieties Grown" },
                { value: "2000+", label: "Happy Customers" },
                { value: "100%", label: "Chemical Free" },
                { value: "04hr", label: "Farm to Table" }
              ].map((stat) => (
                <div key={stat.label} className="bg-white/10 border border-white/10 rounded-2xl p-5 text-center">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-praroha-terracotta">{stat.value}</p>
                  <p className="text-white/60 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Meet Our Founders */}
      <section className="py-8 md:py-10 bg-background" data-testid="founders-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4">
              Leadership
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
              Meet Our Founders
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Dr. Madhu Salumuri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="group h-full bg-white border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/50">
                    <div className="w-14 h-14 shrink-0 rounded-full overflow-hidden">
                      <img src="https://customer-assets.emergentagent.com/job_cf48c9a0-b028-44b3-900f-42fac6806232/artifacts/39nto261_WhatsApp%20Image%202026-03-19%20at%2014.09.24%20%283%29.jpeg" alt="Dr. Madhu Salumuri" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-praroha-forest">Dr. Madhu Salumuri</h3>
                      <p className="text-xs text-praroha-sage">Founder</p>
                    </div>
                  </div>
                  <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
                    <p>
                      Dr. Madhu Salumuri holds a Ph.D. in Advanced Composites from JNTU, Kakinada, an M.E. in Machine Design from BMS College of Engineering, and a B.E. in Mechanical Engineering from Siddaganga Institute of Technology, Tumkur.
                    </p>
                    <p>
                      A distinguished academician and researcher with over 25 years of experience spanning teaching, research, and industry. He served as a Professor at Anurag University, holding leadership roles as Director of the Anurag Centre for Educational Innovations and NIRF Nodal Officer.
                    </p>
                    <p>
                      His passion for health led him to explore microgreens, recognizing their nutritional and medicinal value, and promoting them as a source of wellness.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Mrs. Sumalatha Salumuri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="group h-full bg-white border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/50">
                    <div className="w-14 h-14 shrink-0 rounded-full overflow-hidden">
                      <img src="https://customer-assets.emergentagent.com/job_cf48c9a0-b028-44b3-900f-42fac6806232/artifacts/codl2wkz_WhatsApp%20Image%202026-03-19%20at%2014.09.24%20%282%29.jpeg" alt="Mrs. Sumalatha Salumuri" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-praroha-forest">Mrs. Sumalatha Salumuri</h3>
                      <p className="text-xs text-praroha-sage">Founder</p>
                    </div>
                  </div>
                  <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
                    <p>
                      Sumalatha Salumuri holds an M.Sc in Microbiology from Nagarjuna University and an MBA in Finance from St. Ann's PG College for Women, Osmania University.
                    </p>
                    <p>
                      Her career includes serving as the Vice Director at NAFYAD School in Ethiopia and as Director (Operations) at Reena Sterilz Private Ltd. Fluent in Telugu, English, and Hindi, she brings excellent communication and leadership skills.
                    </p>
                    <p>
                      Her strong health consciousness inspired her to explore microgreens cultivation, recognizing their immense nutritional value. She now promotes sustainable, nutrient-rich diets to support overall wellness.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Nishanth Salumuri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="group h-full bg-white border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/50">
                    <div className="w-14 h-14 shrink-0 rounded-full overflow-hidden">
                      <img src="https://customer-assets.emergentagent.com/job_cf48c9a0-b028-44b3-900f-42fac6806232/artifacts/784rgpx7_freepik__create-an-ultrarealistic-front-portrait-preserving__77041.png" alt="Nishanth Salumuri" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-praroha-forest">Nishanth Salumuri</h3>
                      <p className="text-xs text-praroha-sage">Co-Founder</p>
                    </div>
                  </div>
                  <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
                    <p>
                      Nishanth Salumuri holds a B.Tech in Electronics and Communication Engineering from MIT World Peace University. He previously served as an Assistant Manager – Field Service at Tata Play Fiber.
                    </p>
                    <p>
                      Known for his discipline, adaptability, and ability to perform in dynamic environments, Nishanth consistently brings a results-driven mindset to his work.
                    </p>
                    <p>
                      A passionate runner and sports enthusiast, he embodies resilience, focus, and teamwork—qualities that strongly influence both his professional journey and personal pursuits.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Praroha Section */}
      <section className="py-8 md:py-10 bg-background" data-testid="why-praroha-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-praroha-terracotta/10 text-praroha-terracotta text-sm font-medium rounded-full mb-4">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
              Why Praroha Greens?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group h-full bg-white hover:shadow-hover transition-all duration-300 border-border/50 hover:border-praroha-sage/30">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-4 group-hover:bg-praroha-forest group-hover:scale-110 transition-all duration-300">
                      <value.icon className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-praroha-forest mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
